"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import { SapWasEventListTable } from "../../../components/admin/sap-was-events/SapWasEventListTable";
import {
  getAllEventRegistrations,
  downloadEventRegistrations,
} from "../../../services/sapEventServices";
import { RotateCcw, Download } from "lucide-react";

const SapWasEvents = () => {
  const topRef = useRef(null);
  const queryClient = useQueryClient();

  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    hasPrev: false,
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const statusFilter = "";
  const approvalStatusFilter = "";

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "was-events",
      currentPage,
      entriesOption.value,
      statusFilter,
      approvalStatusFilter,
    ],
    queryFn: () =>
      getAllEventRegistrations({
        page: currentPage,
        limit: entriesOption.value,
        status: statusFilter,
        approvalStatus: approvalStatusFilter,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.registrations) {
      setEvents(data.data.registrations);
      setPagination({
        totalPages: data.data.pagination.totalPages || 1,
        totalCount: data.data.pagination.total || 0,
        hasMore: currentPage < (data.data.pagination.totalPages || 1),
        hasPrev: currentPage > 1,
      });
    }
  }, [data, currentPage]);

  // Function to handle refetch after delete
  const handleRefetchAfterDelete = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: ["was-events"] });
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const result = await downloadEventRegistrations();

      if (result.success) {
        console.log(`File downloaded: ${result.fileName}`);
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert(error.message || "Failed to download file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setCurrentPage(pageNumber);
      scrollToTop();
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentPage(1);
    scrollToTop();
  };

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              <div ref={topRef}></div>

              <Header title="WAS - SAP Event List" desc="Sap event list" />

              <div className="mb-4 flex justify-between items-center">
                <DropdownSelect
                  selected={entriesOption}
                  onChange={handleEntriesChange}
                />

                <div className="flex items-center gap-4">
                  {pagination.totalCount > 0 && (
                    <div className="text-sm text-gray-600">
                      Showing page {currentPage} of {pagination.totalPages} (
                      {pagination.totalCount} total events)
                    </div>
                  )}

                  <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#4F22D8] text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDownloading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Downloading...</span>
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        <span>Export to Excel</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {isLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Loading events...</span>
                </div>
              )}

              {isError && (
                <div className="text-red-500 text-center mt-4">
                  <p>Error loading events: {error.message}</p>
                  <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-4 py-2 mt-2 bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200 mx-auto"
                  >
                    <RotateCcw size={16} />
                    Retry
                  </button>
                </div>
              )}

              {events && !isLoading && (
                <SapWasEventListTable
                  events={events}
                  refetch={handleRefetchAfterDelete}
                  currentIndex={(currentPage - 1) * entriesOption.value}
                  entriesPerPage={entriesOption.value}
                />
              )}

              <div className="h-5" />

              {pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    totalPages={pagination.totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    showPages={5}
                  />
                </div>
              )}

              <div className="h-5" />
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default SapWasEvents;
